<template>
  <div>
    <breadcrumb :items="breadcrumbs" />
    <v-container fluid>
      <div class="d-flex mb-8">
        <h2 class="font-weight-bolder mb-6">{{ $t('finance.loanApplications.loanApplications') }}</h2>
      </div>
      <!-- Data Table Card -->
      <v-card elevation="0" class="px-5 mt-7">
        <v-card-text>
          <v-row style="margin-left: -30px; margin-bottom: 20px; margin-top: 15px;">
            <v-col cols="12">
              <v-data-table
                :headers="tableHeaders"
                :items="applications"
                :items-per-page="options.itemsPerPage"
                :footer-props="{ 'items-per-page-options': itemsPerPageOptions }"
                item-key="id"
                hide-default-footer
                :loading="loading"
                :options.sync="options"
                :server-items-length="totalApplications"
                :loading-text="$t('finance.loanApplications.loadingData')"
                @click:row="handleRowClick"
              >
                <template v-slot:top>
                  <div class="d-flex">
                    <!-- Search Field -->
                    <div class="ml-3" style="width: 280px;">
                      <label for="text">&nbsp;</label>
                      <v-text-field
                        prepend-inner-icon="mdi-magnify"
                        outlined
                        :placeholder="$t('finance.loanApplications.search')"
                        dense
                        v-model="search"
                        @input="searchQuery"
                        class="shrink"
                      ></v-text-field>
                    </div>
                    <!-- Loan Program Dropdown -->
                    <div class="ml-3">
                      <label for="text">{{ $t('finance.loanApplications.loanProgram') }}</label>
                      <v-select
                        v-model="selectedLoanProgram"
                        :items="loanProgramOptions"
                        item-text="name"
                        item-value="value"
                        outlined
                        dense
                        :placeholder="$t('finance.loanApplications.select')"
                        @change="handleLoanProgramChange"
                        style="width: 180px;"
                        class="shrink"
                        clearable
                      ></v-select>
                    </div>
                    <!-- Status Dropdown -->
                    <div class="ml-3">
                      <label for="text">{{ $t('finance.loanApplications.status') }}</label>
                      <v-select
                        v-model="selectedStatus"
                        :items="statusOptions"
                        item-text="name"
                        item-value="value"
                        outlined
                        dense
                        :placeholder="$t('finance.loanApplications.select')"
                        @change="handleStatusChange"
                        style="width: 180px;"
                        class="shrink"
                        clearable
                      ></v-select>
                    </div>
                    <v-spacer></v-spacer>
                    <!-- Pagination Info -->
                    <div class="py-1 title">
                      {{ fromApplications }} - {{ toApplications }} {{ $t('finance.loanApplications.of') }} {{ totalApplications }}
                    </div>
                    <!-- Pagination Controls -->
                    <v-btn
                      class="mx-2"
                      fab
                      small
                      outlined
                      color="primary"
                      :disabled="options.page <= 1"
                      @click="pageChange(false)"
                    >
                      <v-icon dark>mdi-chevron-left</v-icon>
                    </v-btn>
                    <v-btn
                      class="mx-2"
                      fab
                      small
                      outlined
                      color="primary"
                      :disabled="options.page >= totalPages"
                      @click="pageChange(true)"
                    >
                      <v-icon dark>mdi-chevron-right</v-icon>
                    </v-btn>
                    <!-- Customization Menu -->
                    <v-menu v-model="menu" :close-on-content-click="false" :nudge-width="200" left :min-width="417">
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn class="mx-2" fab small outlined color="primary" v-bind="attrs" v-on="on">
                          <v-icon dark>mdi-cog-outline</v-icon>
                        </v-btn>
                      </template>
                      <v-card width="417">
                        <v-list>
                          <v-list-item>
                            <v-list-item-title>{{ $t('finance.loanApplications.customization') }}</v-list-item-title>
                          </v-list-item>
                          <v-divider></v-divider>
                          <div class="pl-3 pt-2 font-weight-normal">
                            {{ $t('finance.loanApplications.recordsPerPage') }}
                          </div>
                          <v-list-item>
                            <v-slider
                              v-model="itemsPerPageSlider"
                              :tick-labels="itemsPerPageOptions"
                              :max="3"
                              step="1"
                              ticks="always"
                              tick-size="4"
                            ></v-slider>
                          </v-list-item>
                          <v-divider class="mt-n3"></v-divider>
                        </v-list>
                        <v-card-text>
                          <v-container fluid>
                            <v-row>
                              <v-col class="mt-n9" cols="6" v-for="header in headers" :key="header.value">
                                <v-checkbox
                                  v-if="header.text"
                                  v-model="selectedHeaders"
                                  :label="header.text"
                                  color="primary"
                                  :value="header.text"
                                  hide-details
                                ></v-checkbox>
                              </v-col>
                            </v-row>
                          </v-container>
                        </v-card-text>
                        <v-layout justify-center>
                          <v-card-actions class="mb-5">
                            <v-spacer></v-spacer>
                            <v-btn outlined color="primary" @click="resetDefault" width="190">
                              {{ $t('finance.loanApplications.restoreDefault') }}
                            </v-btn>
                            <v-btn color="primary" @click="resetTableStructure" width="190">
                              {{ $t('finance.loanApplications.apply') }}
                            </v-btn>
                          </v-card-actions>
                        </v-layout>
                      </v-card>
                    </v-menu>
                    <!-- Export and Print Buttons -->
                    <v-tooltip top color="black" max-width="350">
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          class="mx-2"
                          fab
                          small
                          outlined
                          color="primary"
                          v-bind="attrs"
                          v-on="on"
                          @click="exportToPDF"
                        >
                          <v-icon dark>mdi-download</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ $t('finance.loanApplications.download') }}</span>
                    </v-tooltip>
                    <v-tooltip top color="black" max-width="350">
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          class="mx-2"
                          fab
                          small
                          outlined
                          color="primary"
                          v-bind="attrs"
                          v-on="on"
                          @click="printPDF"
                        >
                          <v-icon dark>mdi-printer</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ $t('finance.loanApplications.print') }}</span>
                    </v-tooltip>
                  </div>
                </template>
                <!-- Application ID & Submission Date Column -->
                <template v-slot:item.applicationIdDate="{ item }">
                  <div class="d-flex flex-column application-id-date-cell">
                    <span class="table-cell-text">{{ item.applicationId }}</span>
                    <span class="table-cell-text text">{{ item.submissionDate }}</span>
                  </div>
                </template>
                <!-- Applicant Details Column -->
                <template v-slot:item.applicantDetails="{ item }">
                  <div class="d-flex flex-column">
                    <span class="table-cell-text">{{ item.applicantName }}</span>
                    <span class="table-cell-text text">{{ item.applicantEmail }}</span>
                  </div>
                </template>
                <!-- Loan Program Column -->
                <template v-slot:item.loanProgram="{ item }">
                  <div class="d-flex flex-column">
                    <span class="table-cell-text">{{ item.programName }}</span>
                    <span class="table-cell-text text">{{ item.institutionName }}</span>
                  </div>
                </template>
                <!-- Loan Amount Column -->
                <template v-slot:item.loanAmount="{ item }">
                  <span class="table-cell-text">{{ item.loanAmountFormatted }}</span>
                </template>
                <!-- Status Column -->
                <template v-slot:item.status="{ item }">
                  <div class="d-flex align-center">
                    <v-btn
                      small
                      rounded
                      depressed
                      :class="getStatusClass(item.status)"
                      height="40"
                      class="status-pill-fixed text-capitalize"
                    >
                      {{ getStatusText(item.status) }}
                    </v-btn>
                    <img
                      v-if="item.status === 'pending'"
                      src="/icons/dds/updaterequired.svg"
                      class="ml-1"
                      style="width: 34px; height: 34px; border-radius: 50%; cursor: pointer;"
                      @click.stop="refreshStatus(item)"
                    />
                  </div>
                </template>
                <template v-slot:footer></template>
              </v-data-table>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Status Legend -->
      <div class="status-legend mt-8 pt-6">
        <h3 class="status-legend-title font-weight-bold mb-4">{{ $t('finance.loanApplications.statusLegend') }}</h3>
        <div class="d-flex flex-wrap">
          <div
            v-for="(item, index) in statusLegendItems"
            :key="index"
            class="d-flex align-center mr-6 mb-2"
          >
            <v-icon v-if="item.icon" :color="item.iconColor || 'orange'" small class="mr-2">
              {{ item.icon }}
            </v-icon>
            <span class="text-body-2 grey--text text--darken-2">{{ $t(item.text) }}</span>
          </div>
        </div>
      </div>
    </v-container>
  </div>
</template>

<script>
import loadingMixin from '@/mixins/LoadingMixin';
import download from '@/mixins/DownloadMixin';
import html2pdf from 'html2pdf.js';
import printJS from 'print-js';
import moment from 'moment';
import FinanceServiceV2 from '@/_services/FinanceServiceV2';

export default {
  created() {
    this.loadLoanApplications();
  },
  async mounted() {
    this.tableHeaders = this.headers;
    this.selectedHeaders = this.allSelectedHeaders;
    this.loadLoanPrograms();
  },
  computed: {
    totalPages() {
      const per = this.options.itemsPerPage || 10;
      return Math.max(1, Math.ceil(this.totalApplications / per));
    },
  },
  data() {
    return {
      debounceTimer: null,
      fromApplications: 0,
      toApplications: 0,
      search: '',
      selectedLoanProgram: '',
      selectedStatus: '',
      loading: false,
      options: {
        page: 1,
        itemsPerPage: 10,
      },
      headers: [
        {
          text: this.$t('finance.loanApplications.applicationIdAndSubmissionDate'),
          align: 'start',
          value: 'applicationIdDate',
          class: 'black--text',
          sortable: true,
        },
        {
          text: this.$t('finance.loanApplications.applicantDetails'),
          align: 'start',
          value: 'applicantDetails',
          class: 'black--text',
          sortable: true,
        },
        {
          text: this.$t('finance.loanApplications.loanProgram'),
          align: 'start',
          value: 'loanProgram',
          class: 'black--text',
          sortable: true,
        },
        {
          text: this.$t('finance.loanApplications.loanAmount'),
          align: 'start',
          value: 'loanAmount',
          class: 'black--text',
          sortable: true,
        },
        {
          text: this.$t('finance.loanApplications.status'),
          align: 'start',
          value: 'status',
          class: 'black--text',
          sortable: true,
        },
      ],
      allSelectedHeaders: [
        this.$t('finance.loanApplications.applicationIdAndSubmissionDate'),
        this.$t('finance.loanApplications.applicantDetails'),
        this.$t('finance.loanApplications.loanProgram'),
        this.$t('finance.loanApplications.loanAmount'),
        this.$t('finance.loanApplications.status'),
      ],
      menu: false,
      breadcrumbs: [
        {
          text: this.$t('finance.loanApplications.financeManagement'),
          disabled: false,
          href: '/finance-management',
          exact: true,
        },
        {
          text: this.$t('finance.loanApplications.loanApplications'),
          disabled: true,
          href: '/finance-management/loan-applications',
        },
      ],
      applications: [],
      totalApplications: 0,
      itemsPerPageOptions: [5, 10, 25, 50],
      itemsPerPageSlider: 1,
      selectedHeaders: [],
      tableHeaders: [],
      loanProgramOptions: [],
      statusLegendItems: [
        {
          icon: 'mdi-refresh',
          iconColor: 'orange',
          text: 'finance.loanApplications.additionalInformationRequested',
        },
      ],
      statusOptions: [
        { name: this.$t('finance.loanApplications.select'), value: '' },
        { name: this.$t('finance.loanApplications.pending'), value: 'pending' },
        { name: this.$t('finance.loanApplications.sentToBank'), value: 'sent_to_bank' },
        { name: this.$t('finance.loanApplications.waitingSignatures'), value: 'waiting_signatures' },
        { name: this.$t('finance.loanApplications.approved'), value: 'approved' },
        { name: this.$t('finance.loanApplications.rejected'), value: 'rejected' },
      ],
    };
  },
  methods: {
    async loadLoanPrograms() {
      let response = await FinanceServiceV2.getLoanPrograms({});
      let payload = response.data || {};
      this.loanProgramOptions = payload.rows.map((row) => ({ name: row.programTitle, value: row.id }));
    },
    normalizeLoanApplicationRow(row) {
      const first = (row.applicantFirstName || '').trim();
      const last = (row.applicantLastName || '').trim();
      const fromApplication = [first, last].filter(Boolean).join(' ').trim();
      const applicant = row.applicant || {};
      const fromUser = [applicant.firstName, applicant.lastName].filter(Boolean).join(' ').trim();
      const applicantName =
        fromApplication || applicant.fullName || fromUser || '—';

      const currency = row.loanProgram && row.loanProgram.loanCurrency;
      const amt = row.loanAmount;
      let loanAmountFormatted = '—';
      if (amt != null && amt !== '') {
        const num = Number(amt);
        const formatted = Number.isFinite(num)
          ? num.toLocaleString(undefined, { maximumFractionDigits: 2 })
          : String(amt);
        loanAmountFormatted = currency ? `${currency} ${formatted}` : formatted;
      }

      const submissionDate = row.createdAt
        ? moment(row.createdAt).format('DD-MMM-YYYY')
        : '';

      return {
        ...row,
        applicantName,
        applicantEmail: row.applicantEmail || applicant.email || '',
        programName: (row.loanProgram && row.loanProgram.programTitle) || '',
        institutionName:
          (row.loanProgram && row.loanProgram.bank && row.loanProgram.bank.name) || '',
        loanAmountFormatted,
        submissionDate,
      };
    },
    syncPaginationRange() {
      const total = this.totalApplications;
      const page = this.options.page;
      const per = this.options.itemsPerPage || 10;
      if (total <= 0) {
        this.fromApplications = 0;
        this.toApplications = 0;
        return;
      }
      this.fromApplications = (page - 1) * per + 1;
      this.toApplications = Math.min(page * per, total);
    },
    async loadLoanApplications() {
      this.startLoading();
      try {
        const per = this.options.itemsPerPage || 10;
        const params = {
          page: this.options.page,
          limit: per,
          search: this.search,
          loanProgramId: this.selectedLoanProgram,
          status: this.selectedStatus,
        };
        let response = await FinanceServiceV2.getLoanApplications(params);
        let payload = response.data || {};
        let rows = payload.rows || [];
        let count = typeof payload.count === 'number' ? payload.count : rows.length;
        if (params.page > 1 && rows.length === 0 && count > 0) {
          const lastPage = Math.max(1, Math.ceil(count / per));
          this.options.page = lastPage;
          response = await FinanceServiceV2.getLoanApplications({ ...params, page: lastPage });
          payload = response.data || {};
          rows = payload.rows || [];
          count = typeof payload.count === 'number' ? payload.count : rows.length;
        }
        this.applications = rows.map((row) => this.normalizeLoanApplicationRow(row));
        this.totalApplications = count;
        this.syncPaginationRange();
      } catch (error) {
        console.error('Error loading loan applications:', error);
      } finally {
        this.stopLoading();
      }
    },
    async resetDefault() {
      this.tableHeaders = this.headers;
      this.selectedHeaders = this.allSelectedHeaders;
      this.options.itemsPerPage = this.itemsPerPageOptions[this.itemsPerPageSlider];
      this.menu = false;
      this.loadLoanApplications();
    },
    async resetTableStructure() {
      this.tableHeaders = this.headers.filter((header) => this.selectedHeaders.includes(header.text));
      this.options.itemsPerPage = this.itemsPerPageOptions[this.itemsPerPageSlider];
      this.loadLoanApplications();
    },
    handleLoanProgramChange() {
      this.options.page = 1;
      this.loadLoanApplications();
    },
    handleStatusChange() {
      this.options.page = 1;
      this.loadLoanApplications();
    },
    searchQuery() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.options.page = 1;
        this.loadLoanApplications();
      }, 500);
    },
    async exportToPDF() {
      this.startLoading();
      try {
        const element = document.querySelector('.v-data-table__wrapper');
        const opt = {
          margin: 1,
          filename: 'LoanApplications.pdf',
          image: { type: 'jpeg', quality: 0.95 },
          html2canvas: { scale: 2, useCORS: true },
          jsPDF: { unit: 'in', format: 'a4', orientation: 'landscape' },
        };
        await html2pdf().set(opt).from(element).save();
      } catch (error) {
        console.error('Export error:', error);
      } finally {
        this.stopLoading();
      }
    },
    async printPDF() {
      const element = document.querySelector('.v-data-table__wrapper');
      const opt = {
        margin: 1,
        filename: 'LoanApplications.pdf',
        image: { type: 'jpeg', quality: 0.95 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'in', format: 'a4', orientation: 'landscape' },
      };
      const worker = await html2pdf()
        .set(opt)
        .from(element)
        .toCanvas()
        .outputPdf('blob', 'LoanApplications.pdf');
      const file = new File([worker], 'LoanApplications.pdf', { type: 'application/pdf' });
      const pdfUrl = await URL.createObjectURL(file);
      await printJS({ printable: `${pdfUrl}`, type: 'pdf', showModal: true });
    },
    pageChange(next) {
      if (next) this.options.page += 1;
      else this.options.page -= 1;
      this.loadLoanApplications();
    },
    getStatusText(status) {
      switch (status) {
        case 'pending':
          return this.$t('finance.loanApplications.pending');
        case 'sent_to_bank':
          return this.$t('finance.loanApplications.sentToBank');
        case 'waiting_signatures':
          return this.$t('finance.loanApplications.waitingSignatures');
        case 'approved':
          return this.$t('finance.loanApplications.approved');
        case 'rejected':
          return this.$t('finance.loanApplications.rejected');
        default:
          return status || this.$t('finance.loanApplications.notApplicable');
      }
    },
    getStatusClass(status) {
      switch (status) {
        case 'pending':
          return 'status-pending';
        case 'sent_to_bank':
          return 'status-sent-to-bank';
        case 'waiting_signatures':
          return 'status-waiting-signatures';
        case 'approved':
          return 'status-approved';
        case 'rejected':
          return 'status-rejected';
        default:
          return 'status-pending';
      }
    },
    refreshStatus(item) {
      // Placeholder: recheck status for pending application
      console.log('Refresh status for application', item.id);
    },
    handleRowClick(item) {
      // Navigate to application details when the route is added
      this.$router.push({ name: 'loanApplicationDetails', params: { id: item.id } }).catch(() => {});
    },
  },
  mixins: [loadingMixin, download],
};
</script>

<style lang="scss" scoped>
.status-pending {
  background-color: #e0e0e0 !important;
  border: 2px solid #9e9e9e !important;
  color: #424242 !important;
}

.status-waiting-signatures {
  background-color: #fff8e1 !important;
  border: 2px solid #ffc107 !important;
  color: #f57f17 !important;
}

.status-sent-to-bank {
  background-color: #b3e5fc !important;
  border: 2px solid #0288d1 !important;
  color: #01579b !important;
}

.status-approved {
  background-color: #c1ffe7 !important;
  border: 2px solid #00bd73 !important;
  color: #489e16 !important;
}

.status-rejected {
  background-color: #ffcdd2 !important;
  border: 2px solid #e53935 !important;
  color: #b71c1c !important;
}

::v-deep .v-data-table-header span {
  text-transform: uppercase !important;
  color: white !important;
  font-weight: bold !important;
}

::v-deep .v-data-table > .v-data-table__wrapper > table > thead > tr > th {
  background-color: #00bd73 !important;
  color: white !important;
}

::v-deep .v-data-table__wrapper tbody tr {
  cursor: pointer;
}

.status-legend-title {
  color: #424242;
  font-size: 1rem;
}
</style>
